
import { supabase } from '@/integrations/supabase/client';
import { BrandRole, BrandTeamMember, SystemRole } from '../types';

export const teamMemberService = {
  async getTeamMembers(brandId: string): Promise<BrandTeamMember[]> {
    const { data: accessData, error } = await supabase
      .from('user_brand_access')
      .select(`
        *,
        custom_roles!custom_role_id (
          id,
          name,
          description,
          permissions
        )
      `)
      .eq('brand_id', brandId)
      .order('role', { ascending: true })
      .order('granted_at', { ascending: false });

    if (error) throw error;
    if (!accessData || accessData.length === 0) return [];

    // Get all unique user IDs
    const userIds = [...new Set([
      ...accessData.map(item => item.user_id),
      ...accessData.map(item => item.granted_by)
    ])];

    // Fetch profiles for all users
    const { data: profilesData, error: profilesError } = await supabase
      .from('profiles')
      .select('id, email, full_name')
      .in('id', userIds);
    
    if (profilesError) {
      console.warn('Could not fetch profiles:', profilesError);
    }

    // Fetch system roles for all team member user IDs
    const teamMemberUserIds = accessData.map(item => item.user_id);
    const { data: systemRolesData, error: systemRolesError } = await supabase
      .rpc('get_users_system_roles', { _user_ids: teamMemberUserIds });

    if (systemRolesError) {
      console.warn('Could not fetch system roles:', systemRolesError);
    }

    // Create maps for easy lookup
    const profilesMap = new Map(
      (profilesData || []).map(profile => [profile.id, profile])
    );
    
    const systemRolesMap = new Map(
      (systemRolesData || []).map((sr: { user_id: string; role: SystemRole }) => [sr.user_id, sr.role])
    );

    // Combine the data
    return accessData.map(item => ({
      id: item.id,
      user_id: item.user_id,
      brand_id: item.brand_id,
      role: item.role as BrandRole,
      custom_role_id: item.custom_role_id,
      granted_by: item.granted_by,
      granted_at: item.granted_at,
      created_at: item.created_at,
      updated_at: item.updated_at,
      system_role: systemRolesMap.get(item.user_id) || null,
      profiles: profilesMap.get(item.user_id) ? {
        email: profilesMap.get(item.user_id)!.email || '',
        full_name: profilesMap.get(item.user_id)!.full_name || ''
      } : undefined,
      granted_by_profile: profilesMap.get(item.granted_by) ? {
        email: profilesMap.get(item.granted_by)!.email || '',
        full_name: profilesMap.get(item.granted_by)!.full_name || ''
      } : undefined,
      custom_role: item.custom_roles ? {
        id: item.custom_roles.id,
        name: item.custom_roles.name,
        description: item.custom_roles.description,
        permissions: Array.isArray(item.custom_roles.permissions) 
          ? item.custom_roles.permissions.filter((p): p is string => typeof p === 'string')
          : []
      } : undefined,
    }));
  },

  async updateMemberRole(memberId: string, newRole: BrandRole, customRoleId?: string): Promise<boolean> {
    const updateData: any = { role: newRole };
    if (customRoleId !== undefined) {
      updateData.custom_role_id = customRoleId || null;
    }

    const { error } = await supabase
      .from('user_brand_access')
      .update(updateData)
      .eq('id', memberId);

    if (error) throw error;
    return true;
  },

  async removeMember(memberId: string): Promise<boolean> {
    const { error } = await supabase
      .from('user_brand_access')
      .delete()
      .eq('id', memberId);

    if (error) throw error;
    return true;
  },

  async updateSystemRole(userId: string, newRole: SystemRole): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase.rpc('set_user_system_role', {
      _user_id: userId,
      _role: newRole,
      _requesting_user_id: user.id
    });

    if (error) throw error;
    return true;
  }
};
